import { integer, pgTable, text, uniqueIndex, uuid } from "drizzle-orm/pg-core";
import { createInsertSchema, createSelectSchema } from "drizzle-zod";
import z from "zod";

import { usStatesEnumSchema, type UsStatesEnumType } from "./commonSchema";
import { customTimestamp } from "./utils/types";

export const fplConstantsTable = pgTable(
  "fpl_constants",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    year: integer("year").notNull(),
    // Stored as text[] rather than us_states_enum[] so GCP Datastream can replicate
    // it to BigQuery (Datastream cannot map arrays of enum types). Values are still
    // validated against usStatesEnumSchema at the API layer below. See issue #512.
    usStates: text("us_states")
      .array()
      .$type<UsStatesEnumType[]>()
      .notNull()
      .default([]),
    individualAmount: integer("individual_amount").notNull(),
    additionalAmount: integer("additional_amount").notNull(),
    percentageSelector: integer("percentage_selector").notNull().default(100),
    createdAt: customTimestamp("created_at")
      .notNull()
      .$defaultFn(() => new Date().toISOString()),
    updatedAt: customTimestamp("updated_at")
      .notNull()
      .$defaultFn(() => new Date().toISOString())
      .$onUpdateFn(() => new Date().toISOString()),
  },
  table => [
    uniqueIndex("fpl_constants_year_amounts_index").on(
      table.year,
      table.individualAmount,
      table.additionalAmount,
    ),
  ],
);

export const fplConstantSchema = createSelectSchema(fplConstantsTable, {
  usStates: z.array(usStatesEnumSchema),
});
export type FplConstantType = z.infer<typeof fplConstantSchema>;

export const insertFplConstantsSchema = createInsertSchema(fplConstantsTable, {
  year: z.number(),
  usStates: z.array(usStatesEnumSchema),
  individualAmount: z.number(),
  additionalAmount: z.number(),
}).omit({
  createdAt: true,
  updatedAt: true,
});

export type InsertFplConstantType = z.infer<typeof insertFplConstantsSchema>;
